import type { HelpArticle, HelpCategory } from "@/lib/registrant-help/types";

export const TITLE_HELP_CATEGORIES: HelpCategory[] = [
  {
    id: "getting-started",
    title: "Getting started",
    summary: "Submit a title request and follow its status.",
  },
  {
    id: "organization",
    title: "Account",
    summary: "Profile details, invites, and primary or secondary users.",
  },
];

export const TITLE_HELP_ARTICLES: HelpArticle[] = [
  {
    id: "submit-a-request",
    title: "Submit a request",
    summary: "Create a title request from the property details you have.",
    category: "getting-started",
    keywords: ["new", "request", "property", "apn", "details"],
    related: ["response-time", "request-statuses", "account-details"],
    sections: [
      {
        heading: "Create a request",
        blocks: [
          {
            type: "p",
            text: "Open New Request... and enter an address, APN, or owner. Add any extra context in Request Details. Your name, email, and phone come from your account.",
          },
        ],
      },
    ],
  },
  {
    id: "request-statuses",
    title: "Request statuses",
    summary:
      "What Pending, No Known Obligation, Active Obligation on Property, Information Required, and Cancelled mean.",
    category: "getting-started",
    keywords: ["status", "pending", "todo", "obligation", "active", "cancel"],
    related: ["submit-a-request"],
    sections: [
      {
        heading: "Statuses",
        blocks: [
          {
            type: "ul",
            items: [
              "Pending: Hera has the request and has not sent an outcome. The request is active.",
              "No Known Obligation: Hera has no known obligation in the property. The request is inactive.",
              "Active Obligation on Property: The jurisdiction has an active obligation on the property and a balance is due. The request is inactive. Raise a followup title request to send it back to Pending.",
              "Information Required: Hera posted a comment asking for more information. The request stays active. Posting a comment returns it to Pending.",
              "Cancelled: You or Hera cancelled the request. It is inactive.",
              "Choose Cancel request while the request is Pending to mark it Cancelled and inactive.",
              "All Active Requests includes Pending and Information Required. Requires Further Information shows only the requests that need more information. Completed Requests are the inactive ones: No Known Obligation, Active Obligation on Property, and Cancelled. A long list switches to a table with an Active column.",
            ],
          },
        ],
      },
    ],
  },
  {
    id: "response-time",
    title: "When you get a response",
    summary: "Hera responds within 10 business days.",
    category: "getting-started",
    keywords: ["response", "time", "days", "email", "when", "how long"],
    related: ["request-statuses", "request-comments"],
    sections: [
      {
        heading: "Response time",
        blocks: [
          {
            type: "p",
            text: "Hera returns a response within 10 business days of your request. The response is emailed to the person who submitted the request and saved on the request under Correspondence, so anyone in your account can open it.",
          },
        ],
      },
    ],
  },
  {
    id: "request-comments",
    title: "Comments on a request",
    summary: "Ask a question or add information on a request you submitted.",
    category: "getting-started",
    keywords: ["comment", "question", "follow up", "message", "reply"],
    related: ["response-time", "submit-a-request"],
    sections: [
      {
        heading: "Add a comment",
        blocks: [
          {
            type: "p",
            text: "Open a request from My requests and scroll to Comments. Type your comment and choose Post Comment. Everyone in your account can read and add comments on the account's requests.",
          },
          {
            type: "p",
            text: "Hera replies on the same request. Replies from Hera are shown as Hera Registry.",
          },
        ],
      },
    ],
  },
  {
    id: "managing-users",
    title: "Managing users",
    summary: "Invite people, choose an admin, and transfer the primary user.",
    category: "organization",
    keywords: ["invite", "admin", "primary", "secondary", "users"],
    related: ["account-details"],
    sections: [
      {
        heading: "Users",
        blocks: [
          {
            type: "p",
            text: "Admins invite users from the Users tab. Everyone in the account sees the same requests. The primary user can make another active user the primary. There is no billing on this portal.",
          },
        ],
      },
    ],
  },
  {
    id: "account-details",
    title: "Account details",
    summary: "Name, phone, company, and the organization address.",
    category: "organization",
    keywords: ["account", "profile", "address", "phone"],
    related: ["managing-users", "submit-a-request"],
    sections: [
      {
        heading: "Your profile",
        blocks: [
          {
            type: "p",
            text: "The primary user keeps the organization address. Secondary users can update their name and phone. Email stays the address you used to sign in.",
          },
        ],
      },
    ],
  },
];

export function getTitleHelpArticle(id: string): HelpArticle | undefined {
  return TITLE_HELP_ARTICLES.find((article) => article.id === id);
}

export function isTitleHelpArticleId(id: string): boolean {
  return TITLE_HELP_ARTICLES.some((article) => article.id === id);
}

export function titleArticlesInCategory(
  categoryId: HelpCategory["id"],
): HelpArticle[] {
  return TITLE_HELP_ARTICLES.filter(
    (article) => article.category === categoryId,
  );
}
